import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { PaginationSearchParams, SearchQuery, SortOrderQuery } from '@/api/types';
import { api } from '@/api';
import { DEFAULT_INFINITE_QUERY_OPTIONS } from '@/queries/utils';
import { getAirportById, seedAirportsList } from './airports.fetcher';
import { airportsQueryKeys } from './airports.keys';

export type AirportsQueryFilters = {
  name: string;
  ids: string;
  code: string;
  sabreSupportedOnly: boolean;
  withoutFbo: boolean;
};

export const airportsQuery = {
  infiniteList: (searchPagination: PaginationSearchParams & SortOrderQuery & Partial<AirportsQueryFilters>) =>
    infiniteQueryOptions({
      queryKey: airportsQueryKeys.getInfiniteList(searchPagination),
      queryFn: async ({ pageParam: pageIndex }) => {
        const response = await api.airports.getAirportsList({ ...searchPagination, pageIndex });

        seedAirportsList(response.items);

        return response;
      },
      ...DEFAULT_INFINITE_QUERY_OPTIONS,
    }),
  items: (ids: string[], idsFilterType: 'ids' | 'maoIds' = 'ids') =>
    queryOptions({
      queryKey: airportsQueryKeys.getList(ids, idsFilterType),
      queryFn: async () => {
        const response = await api.airports.getAirportsByIds(ids.join(','), idsFilterType);

        seedAirportsList(response.items);

        return response;
      },
      staleTime: Infinity,
    }),
  item: (id: string, idsFilterType: 'ids' | 'maoIds' = 'ids') =>
    queryOptions({
      queryKey: airportsQueryKeys.getItem(id, idsFilterType),
      queryFn: () => getAirportById(id, idsFilterType),
      staleTime: Infinity,
    }),
};

const getDefaultAirportsParams = ({ asc, orderBy }: SortOrderQuery) => ({
  itemsPerPage: 10,
  pageIndex: 0,
  asc: asc || true,
  orderBy: orderBy || 'code',
});

const useInfiniteAirportsListQueryByCode = ({
  searchQuery,
  sabreSupportedOnly,
  withoutFbo,
  ...sortData
}: SearchQuery & SortOrderQuery & Partial<AirportsQueryFilters>) => {
  const defaultParams = getDefaultAirportsParams(sortData);

  return useInfiniteQuery(
    airportsQuery.infiniteList({
      ...defaultParams,
      code: searchQuery || '',
      sabreSupportedOnly,
      withoutFbo,
    }),
  );
};

const useInfiniteAirportsListQueryByName = ({
  searchQuery,
  sabreSupportedOnly,
  withoutFbo,
  ...sortData
}: SearchQuery & SortOrderQuery & Partial<AirportsQueryFilters>) => {
  const defaultParams = getDefaultAirportsParams(sortData);

  return useInfiniteQuery(
    airportsQuery.infiniteList({
      ...defaultParams,
      name: searchQuery || '',
      sabreSupportedOnly,
      withoutFbo,
    }),
  );
};

const useAirportById = (ids: string[], idsFilterType: 'ids' | 'maoIds' = 'ids') => {
  return useQuery(airportsQuery.items(ids, idsFilterType));
};

export { useInfiniteAirportsListQueryByCode, useInfiniteAirportsListQueryByName, useAirportById };
